
import React, { useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { useCardCommentsWithReplies } from '@/hooks/comments/useCardCommentsWithReplies';
import CommentForm from './comment/CommentForm';
import CommentSortControls, { SortOption } from './comment/CommentSortControls';
import VisualCommentThread from './comment/VisualCommentThread';
import CommentFilters from './comment/CommentFilters';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';

interface CardCommentsSectionProps {
  cardId: string;
  initialLimit?: number;
  enableFiltering?: boolean;
  enableSearch?: boolean;
}

const CardCommentsSection: React.FC<CardCommentsSectionProps> = ({
  cardId,
  initialLimit = 10,
  enableFiltering = true,
  enableSearch = true
}) => {
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  
  const {
    // Core data
    comments,
    totalComments,
    isLoading,
    isSubmitting,
    handleSubmitComment,
    
    // Pagination - using client-side pagination for "Load More" functionality
    loadMoreComments,
    hasMoreComments,
    
    // Voting
    userVotes,
    upvoteCounts,
    downvoteCounts,
    maxVotes,
    isVoting,
    handleVote,
    
    // Authentication
    isAuthenticated,
    
    // Reply functionality
    replies,
    repliesVisible,
    repliesLoading,
    repliesPagination,
    replyForm,
    toggleReplies,
    loadMoreReplies,
    toggleReplyForm,
    
    // Enhanced features
    filter,
    setFilter,
    searchQuery,
    handleSearch,
    isSearching,
    reportComment,
    
    // Utilities
    refetch
  } = useCardCommentsWithReplies({
    cardId,
    limit: initialLimit,
    sortBy: sortBy === 'votes' ? 'top' : sortBy,
    serverSidePagination: false, // Use client-side pagination for "Load More"
    enableFiltering,
    enableSearch
  });

  const hasComments = totalComments > 0;
  const showLoadMore = hasMoreComments && comments.length > 0;

  const handleClearFilters = () => {
    setFilter({ type: 'all' });
    handleSearch('');
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Comments ({totalComments})
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Comment Form */}
        <CommentForm
          onSubmit={handleSubmitComment}
          isSubmitting={isSubmitting}
          isAuthenticated={isAuthenticated}
          placeholder="Share your thoughts on this insight..."
        />

        {/* Filters and Search */}
        {(enableFiltering || enableSearch) && (
          <>
            <Separator />
            <CommentFilters
              filter={filter}
              searchQuery={searchQuery}
              isSearching={isSearching}
              totalComments={totalComments}
              onFilterChange={setFilter}
              onSearchChange={handleSearch}
              onClearFilters={handleClearFilters}
            />
          </>
        )}

        {/* Sort Controls */}
        {hasComments && (
          <>
            <Separator />
            <CommentSortControls
              sortBy={sortBy}
              onSortChange={setSortBy}
            />
          </>
        )}

        {/* Comments with Enhanced Threading */}
        <div className="space-y-6">
          {isLoading && comments.length === 0 ? (
            <div className="text-center py-8">
              <div className="animate-pulse space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="border rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <div className="h-8 w-8 bg-muted rounded-full" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-muted rounded w-1/4" />
                        <div className="h-4 bg-muted rounded w-3/4" />
                        <div className="h-4 bg-muted rounded w-1/2" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : hasComments ? (
            <>
              {comments.map((comment) => (
                <VisualCommentThread
                  key={comment.id}
                  comment={comment}
                  replies={replies[comment.id] || []}
                  isRepliesVisible={repliesVisible[comment.id] || false}
                  isRepliesLoading={repliesLoading[comment.id] || false}
                  hasMoreReplies={repliesPagination[comment.id]?.hasMore || false}
                  replyForm={replyForm}
                  userVotes={userVotes}
                  upvoteCounts={upvoteCounts}
                  downvoteCounts={downvoteCounts}
                  maxVotes={maxVotes}
                  isVoting={isVoting}
                  isAuthenticated={isAuthenticated}
                  isSubmitting={isSubmitting}
                  onVote={handleVote}
                  onToggleReplies={() => toggleReplies(comment.id)}
                  onLoadMoreReplies={() => loadMoreReplies(comment.id)}
                  onToggleReplyForm={toggleReplyForm}
                  onSubmitComment={handleSubmitComment}
                  onReportComment={reportComment}
                />
              ))}
              
              {/* Load More Button - similar to polls section */}
              {showLoadMore && (
                <div className="flex justify-center pt-6">
                  <Button 
                    variant="outline" 
                    onClick={loadMoreComments}
                    disabled={isLoading}
                    className="px-8 py-2"
                  >
                    {isLoading ? 'Loading...' : 'Load More Comments'}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground text-lg font-medium mb-2">
                No comments yet
              </p>
              <p className="text-muted-foreground text-sm">
                {searchQuery || filter.type !== 'all' 
                  ? 'Try adjusting your filters or search terms'
                  : 'Be the first to share your thoughts on this insight!'
                }
              </p>
              {(searchQuery || filter.type !== 'all') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearFilters}
                  className="mt-4"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CardCommentsSection;
